/* 同步協定：線路上流動的東西長什麼樣。
   伺服器只看得到：opId、deviceId、時間、序號，以及一段密文。
   實體類型、欄位、值全部在密文裡 —— 這是有意的。 */
import type { EntityType, FieldDelta, OpReason, SyncOp } from "../domain/types.ts";
import type { Sealed } from "../crypto/box.ts";

export const SYNC_VERSION = 1;
export const MAX_OPS_PER_PUSH = 200;
export const MAX_BLOB_BYTES = 256 * 1024;
export const DEFAULT_PULL_LIMIT = 200;

/** 上傳的 op：明碼部分只有「誰、何時、哪一筆」，其餘全是密文。 */
export interface WireOp {
  opId: string;
  deviceId: string;
  at: number;
  /** 伺服器指派的單調序號；上傳時為 0，由伺服器填。 */
  serverSeq: number;
  blob: Sealed;
}

/** 密文裡的內容。 */
export interface OpPayload {
  entityType: EntityType;
  entityId: string;
  baseRev: number;
  changedFields: Record<string, FieldDelta>;
  reason: OpReason;
  seq: number;
}

export interface PushRequest { deviceId: string; ops: WireOp[] }
export interface PushResponse { accepted: number; lastSeq: number; duplicates: number }
export interface PullResponse { ops: WireOp[]; lastSeq: number; more: boolean }

export interface HealthResponse { ok: boolean; version: number; head: number; accountCreated: boolean }

export const SYNC_PATHS = {
  health: "/v1/health",
  keys: "/v1/keys",
  ops: "/v1/ops",
} as const;

export type ValidationResult = { ok: true; op: WireOp } | { ok: false; error: string };

/** 伺服器端的嚴格驗證：拒絕任何形狀不對的東西，不猜、不修。 */
export function validateWireOp(input: unknown): ValidationResult {
  if (typeof input !== "object" || input === null) return { ok: false, error: "op 必須是物件" };
  const o = input as Record<string, unknown>;
  if (typeof o["opId"] !== "string" || o["opId"].length < 8 || o["opId"].length > 64)
    return { ok: false, error: "opId 不合法" };
  if (typeof o["deviceId"] !== "string" || o["deviceId"].length < 4 || o["deviceId"].length > 64)
    return { ok: false, error: "deviceId 不合法" };
  if (typeof o["at"] !== "number" || !Number.isFinite(o["at"]) || o["at"] <= 0)
    return { ok: false, error: "時間戳不合法" };
  const blob = o["blob"] as Record<string, unknown> | undefined;
  if (!blob || typeof blob !== "object") return { ok: false, error: "缺少密文" };
  if (typeof blob["ivB64"] !== "string" || typeof blob["ctB64"] !== "string")
    return { ok: false, error: "密文格式不合法" };
  if (blob["ivB64"].length > 64) return { ok: false, error: "iv 過長" };
  if (blob["ctB64"].length > MAX_BLOB_BYTES) return { ok: false, error: "密文過大" };
  return { ok: true, op: {
    opId: o["opId"], deviceId: o["deviceId"], at: o["at"],
    serverSeq: typeof o["serverSeq"] === "number" ? o["serverSeq"] : 0,
    blob: { ivB64: blob["ivB64"], ctB64: blob["ctB64"] },
  } };
}

export function validatePushRequest(input: unknown): { ok: true; req: PushRequest } | { ok: false; error: string } {
  if (typeof input !== "object" || input === null) return { ok: false, error: "請求必須是物件" };
  const r = input as Record<string, unknown>;
  if (typeof r["deviceId"] !== "string" || r["deviceId"].length < 4)
    return { ok: false, error: "deviceId 不合法" };
  if (!Array.isArray(r["ops"])) return { ok: false, error: "ops 必須是陣列" };
  if (r["ops"].length > MAX_OPS_PER_PUSH) return { ok: false, error: "一次推送太多 op" };
  const ops: WireOp[] = [];
  for (const raw of r["ops"]) {
    const v = validateWireOp(raw);
    if (!v.ok) return { ok: false, error: v.error };
    if (v.op.deviceId !== r["deviceId"]) return { ok: false, error: "op 的 deviceId 與請求不符" };
    ops.push(v.op);
  }
  return { ok: true, req: { deviceId: r["deviceId"], ops } };
}

/** 把本機的 SyncOp 轉成要加密的內容。 */
export function toPayload(op: SyncOp): OpPayload {
  return {
    entityType: op.entityType, entityId: op.entityId, baseRev: op.baseRev,
    changedFields: op.changedFields, reason: op.reason, seq: op.seq,
  };
}

export function fromPayload(opId: string, deviceId: string, at: number, payload: OpPayload): SyncOp {
  return {
    opId, deviceId, seq: payload.seq, at, entityType: payload.entityType, entityId: payload.entityId,
    baseRev: payload.baseRev, changedFields: payload.changedFields, reason: payload.reason,
  };
}
